<template>
    <div
        :class="[
            {
                disabled: isLoading,
            },
            'position-relative'
        ]"
    >
        <div
            v-if="isLoading"
            class="spinner-holder"
        >
            <ui-spinner />
        </div>

        <div class="d-flex align-items-center mb-5">
            <h2 class="mb-0">
                {{ $tc('messages.messages', 2) }}
            </h2>

            <button
                @click="setModal('channel')"
                class="btn btn-primary ml-3"
            >
                <span>
                    {{ $t('messages.new') }}
                </span>
            </button>
        </div>

        <div class="rounded bg-white box-shadow messages-wrapper">
            <div class="row">
                <div class="col-md-4">
                    <div
                        class="
                            overflow-auto
                            channels
                            border-bottom
                            border-solid
                            border-lg-bottom-0
                            border-lg-right
                            border-gray
                        "
                    >
                        <template v-if="channels.length">
                            <div
                                v-for="(channel, index) in channels"
                                :key="channel.id"
                                class="position-relative channel"
                            >
                                <button
                                    @click.prevent="handleChannelEdit(channel)"
                                    class="channel-edit"
                                    :content="$t('messages.channel_edit')"
                                    v-tippy
                                >
                                    <ui-icon icon="edit" />
                                </button>

                                <button
                                    @click="fetchChannel(channel.id)"
                                    :class="
                                        [
                                            {
                                                'active': channel.active,
                                                'border-bottom': (channels.length - 1) !== index,
                                            },
                                            `
                                                w-100
                                                d-flex
                                                flex-column
                                                flex-md-row
                                                p-4
                                                channel-item
                                            `
                                        ]
                                    "
                                >
                                    <ui-avatar
                                        :src="channel.avatar"
                                        size="small"
                                        class="flex-0-0-auto mr-md-3"
                                    />

                                    <div>
                                        <div
                                            class="
                                                mb-2
                                                text-small
                                                text-gray-nickel
                                            "
                                        >
                                            {{ channel.last_update | dateAgo }}
                                        </div>
                                        <h4>
                                            <span class="position-relative">
                                                <span
                                                    v-if="channel.has_unread_messages"
                                                    class="
                                                        position-absolute
                                                        bg-danger
                                                        rounded-circle
                                                        message-notification
                                                    "
                                                ></span>

                                               {{ channel.title }}
                                            </span>
                                        </h4>
                                        <p class="text-smaller line-clamp-2">
                                            {{ channel.excerpt }}
                                        </p>
                                    </div>
                                </button>
                            </div>
                        </template>
                        <p
                            v-else
                            class="p-4 text-gray-nickel"
                        >
                            {{ $t('messages.no_channels') }}
                        </p>
                    </div>
                </div>
                <div class="col-md-8">
                    <div
                        ref="messagesWrapper"
                        class="
                            d-flex
                            flex-column
                            h-100
                            p-4
                            p-lg-0
                            py-lg-4
                            pr-lg-4
                            overflow-auto
                            messages
                        "
                    >
                        <div
                            v-if="Object.keys(channelMessages).length"
                            ref="messages"
                            class="d-flex flex-column justify-content-end"
                        >
                            <div
                                v-for="message in channelMessages"
                                :key="message.id"
                                :class="[
                                    {
                                        'align-self-end active': message.user_id === user.id,
                                    },
                                    `
                                        p-4
                                        mb-5
                                        rounded-lg
                                        message
                                    `
                                ]"
                            >
                                <div class="d-flex align-items-center mb-3 text-small">
                                    <span class="font-weight-bold">
                                        {{ message.title }}
                                    </span>
                                    <span class="ml-2 text-gray-nickel">
                                        {{ message.last_update | dateAgo }}
                                    </span>
                                </div>
                                <p v-html="contentsWithBreaklines(message.content)" />
                            </div>
                        </div>
                        <p v-else>
                            {{ $t('messages.no_results') }}
                        </p>

                        <div
                            v-if="channelActive"
                            class="position-relative"
                        >
                            <textarea
                                v-model="inputMessage"
                                ref="inputElement"
                                rows="5"
                                :class="[
                                    {
                                        'has-break-lines': numberOfBreaklines,
                                    },
                                    `
                                        w-100
                                        flex-1-0-auto
                                        p-3
                                        mt-auto
                                        border
                                        rounded-lg
                                        input
                                    `
                                ]"
                                :placeholder="$t('messages.input')"
                            ></textarea>

                            <button
                                @click="sendMessage"
                                type="button"
                                class="
                                    btn
                                    btn-gray
                                    btn-sm
                                    py-2
                                    px-3
                                    input-button
                                "
                            >
                                <span>
                                    {{ $t('messages.send') }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <portal to="portal">
            <ui-modal
                v-if="modal === 'channel'"
                class="modal-small"
                :title="$t('messages.new')"
            >
                <form
                    @submit.prevent="channelAdd"
                    action="#"
                    method="POST"
                    class="d-flex flex-column"
                >
                    <ui-input
                        v-model="channelNew.title"
                        :label="$t('messages.title')"
                        required
                    />

                    <ui-textarea
                        v-model="channelNew.message"
                        :label="$tc('messages.messages', 1)"
                        rows="5"
                        required
                    />

                    <button class="btn btn-primary align-self-start">
                        <span>
                            {{ $t('general.send') }}
                        </span>
                    </button>
                </form>
            </ui-modal>

            <ui-modal
                v-if="modal === 'channelEdit'"
                class="modal-small"
                :title="$t('messages.channel_edit')"
            >
                <form
                    @submit.prevent="channelUpdate"
                    action="#"
                    method="POST"
                    class="d-flex flex-column"
                >
                    <ui-input
                        v-model="channelEdit.title"
                        :label="$t('messages.title')"
                        required
                    />

                    <button class="btn btn-primary align-self-start">
                        <span>
                            {{ $t('general.save') }}
                        </span>
                    </button>
                </form>
            </ui-modal>
        </portal>
    </div>
</template>

<script>
import autosize from 'autosize';
import axios from 'axios';

import { mapMutations, mapState } from 'vuex';

import UiAvatar from '../ui/avatar.vue';
import UiIcon from '../ui/icon.vue';
import UiInput from '../ui/input.vue';
import UiModal from '../ui/modal.vue';
import UiSpinner from '../ui/spinner.vue';
import UiTextarea from '../ui/textarea.vue';

export default {
    name: 'MessagesIndex',
    components: {
        UiAvatar,
        UiIcon,
        UiInput,
        UiModal,
        UiTextarea,
        UiSpinner,
    },
    props: {
        patientId: {
            type: Number,
            default: null,
        },
        hospitalId: {
            type: Number,
            required: true,
        },
        activeChannel: {
            type: Number,
            default: 0,
        },
        url: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            channelEdit: {},
            channelNew: {
                message: '',
                title: '',
            },
            channelMessages: [],
            channels: [],
            inputMessage: '',
            isLoading: false,
        };
    },
    created() {
        this.fetchChannels(this.activeChannel);
    },
    destroyed() {
        autosize.destroy(this.$refs.inputElement);
    },
    mounted() {
        autosize(this.$refs.inputElement);
    },
    computed: {
        ...mapState('modal', [
            'modal',
        ]),

        channelActive() {
            return this.channels.find(item => item.active);
        },

        numberOfBreaklines() {
            return (this.inputMessage.match(/\n/g) || []).length;
        },
    },
    methods: {
        ...mapMutations('modal', [
            'setModal',
        ]),

        async channelAdd() {
            this.isLoading = true;

            try {
                await axios.post(this.route(
                    'hospitals.patients.messages.channel_add',
                    [this.hospitalId, this.patientId]
                ), {
                    message: this.channelNew.message,
                    title: this.channelNew.title,
                    user_id: this.user.id,
                });
            } finally {
                this.isLoading = false;
            }

            this.channelNew = {
                message: '',
                title: '',
            };

            this.setModal(null);
            this.fetchChannels();
        },

        async channelUpdate() {
            this.isLoading = true;

            try {
                await axios.post(this.route(
                    'hospitals.patients.messages.channel_edit',
                    [this.hospitalId, this.patientId, this.channelEdit.id]
                ), {
                    title: this.channelEdit.title,
                    user_id: this.user.id,
                });
            } finally {
                this.isLoading = false;
            }

            const channelCurrentIndex = this.channels.findIndex(item => item.id === this.channelEdit.id);
            this.channels[channelCurrentIndex] = this.channelEdit;
            this.channelEdit = {};
            this.setModal(null);
        },

        contentsWithBreaklines(message) {
            message = message.replace(/@&lt;([0-9]+)&gt;/gm, '@<$1>');

            return typeof message === 'string' ? message.replace(/\n/g, '<br>') : message;
        },

        async fetchChannels(activeChannel = 0) {
            if (!this.url) return;

            this.isLoading = true;

            const { data } = await axios.get(this.url);

            this.channels = data.map((item) => {
                return {
                    active: activeChannel === item.id,
                    ...item,
                };
            });

            if (this.channels.length) {
                this.fetchChannel(activeChannel);
            }

            this.isLoading = false;
        },

        async fetchChannel(id) {
            if (!id) return;

            this.isLoading = true;

            this.channels.forEach(channel => channel.active = false);
            this.channels.find(item => item.id === id).active = true;

            const { data } = await axios.get(this.route('hospitals.patients.messages.channel_get', [
                this.hospitalId,
                this.patientId,
                id,
            ]));

            this.channelMessages = data;

            this.isLoading = false;
        },

        handleChannelEdit(channel) {
            this.channelEdit = JSON.parse(JSON.stringify(channel));
            this.setModal('channelEdit');
        },

        async sendMessage() {
            const message = this.inputMessage.trim();
            if (message === '') return;

            this.isLoading = true;
            this.inputMessage = '';

            try {
                await axios.post(this.route('hospitals.patients.messages.send', [
                    this.hospitalId,
                    this.patientId,
                    this.channelActive.id,
                ]), { message });
            } finally {
                this.isLoading = false;
            }

            this.channelMessages.push({
                content: message,
                last_update: new Date(),
                title: this.user.name,
                user_id: this.user.id,
            });

            // Reset height of input
            this.$refs.inputElement.style.height = '54px';
        },

        async scrollToBottom() {
            await this.$nextTick();

            if (this.$refs.messages) {
                this.$refs.messagesWrapper.scrollTo(0, this.$refs.messages.clientHeight);
            }
        },
    },
    watch: {
        channelMessages: {
            handler() {
                this.scrollToBottom();
            },
        },
    },
}
</script>

<style lang="scss" scoped>
    .spinner-holder {
        z-index: 2;
    }

    .message {
        max-width: 80%;
        background-color: $light;

        &.active {
            background-color: rgba($primary, 0.1);
        }

        &s {
            max-height: 50vh;
        }
    }

    .channel {
        &s {
            position: sticky;
            top: 0;
            height: 100%;
            max-height: 50vh;
        }

        &-item {
            &.active {
                background-color: $info;
            }
        }

        &-edit {
            position: absolute;
            top: 12px;
            right: 12px;
            padding: 4px;
            color: $primary;
            pointer-events: none;
            opacity: 0;

            &:hover {
                color: $dark;
            }

            .channel:hover & {
                pointer-events: all;
                opacity: 1;
            }
        }
    }

    .input {
        border-color: $input-border-color;

        &-button {
            position: absolute;
            right: 6px;
            bottom: 6px;
            border-radius: 10px;
        }
    }
</style>
